import { styled, keyframes } from '@mui/material/styles';
import {
  Box,
  TextField,
  Typography,
  Card as MuiCard,
  IconButton,
  Link
} from '@mui/material';

const fontFamily = '"SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

export const glowAnimation = keyframes`
  0% { opacity: 0; filter: blur(10px); }
  50% { opacity: 1; filter: blur(0px); }
  100% { opacity: 0; filter: blur(10px); }
`;

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

export const CenteredContainer = styled(Box)(({ theme }) => {
  // Usar background transparente si es modo wallpaper
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center', // Volver a 'center' para centrado vertical
    minHeight: 'calc(100vh - 117px)',
    maxHeight: 'calc(100vh - 117px)',
    padding: theme.spacing(4),
    paddingTop: theme.spacing(4), // Mantener padding superior razonable
    paddingBottom: theme.spacing(8),
    boxSizing: 'border-box',
    fontFamily,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'optimizeLegibility',
    backgroundColor: isWallpaperMode ? 'transparent' : 'var(--bg-primary-elevated)',
    color: 'var(--label-primary)',
    overflowY: 'auto',
    width: '100%',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px'
    },
    // Diferente comportamiento para móvil vs escritorio
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(8),
      justifyContent: 'center', // En móvil, mantener al inicio
      minHeight: 'calc(100vh - 104px)',
      maxHeight: 'calc(100vh - 104px)'
    }
  };
});

export const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily,
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: '0 8px',
    maxWidth: '100%',
    boxSizing: 'border-box'
  }
}));

export const TypedText = styled(Typography)(({ theme }) => {
  const isWallpaper = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '60px',
    lineHeight: 1.08349,
    letterSpacing: '-0.003em',
    fontWeight: 600,
    fontFamily,
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: '28px',
    padding: '0 20px',
    // Blanco en wallpaper, usa variable CSS en otros
    color: isWallpaper ? '#FFFFFF' : 'var(--label-primary)',
    '& span': {
      fontSize: 'inherit !important',
      lineHeight: 'inherit !important',
      letterSpacing: 'inherit !important',
      fontWeight: 'inherit !important',
      fontFamily: 'inherit !important',
    },
    '& .typed-cursor': {
      fontSize: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 'inherit',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '32px',
      lineHeight: 1.2,
      padding: '0 16px',
      marginBottom: '16px'
    }
  };
});

export const StyledForm = styled('form')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '20px',
  position: 'relative',
  fontFamily,
  [theme.breakpoints.down('sm')]: {
    marginBottom: '16px'
  }
}));

// En HomeInputStyles.js
export const moveGradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;
 
// Y luego modifica el StyledTextField
export const StyledTextField = styled(TextField)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const placeholderColor = colors.textSecondary;
  const inputBackground = colors.cardBackground;

  // Definimos el estilo del gradiente como una constante para reutilizarlo
  const gradientBorderStyle = {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: 'inherit',
      padding: '1px', // Espacio para el gradiente
      background: 'linear-gradient(90deg, #69BFF9, #B96AF3, #E9685E, #F2AC3E, #69BFF9)',
      backgroundSize: '500% 100%',
      animation: `${moveGradientAnimation} 8s linear infinite`,
      WebkitMask: 
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none',
      zIndex: 1,
    }
  };
 
  return {
    flexGrow: 1,
    margin: '10px 0',
    maxWidth: '800px',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : inputBackground,
      backgroundClip: 'padding-box',
      borderRadius: '30px',
      minHeight: '57px',
      height: 'auto',
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      transition: 'all 0.2s ease-in-out',
      border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : colors.cardBackground}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0',
      color: colors.textDefault,
      position: 'relative', // Asegúrate de que la posición sea relativa para el pseudo-elemento
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover': {
        backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : colors.cardBackground,
        border: isWallpaperMode
          ? '1px solid #FFFFFF'
          : '1px solid transparent',
        ...(isWallpaperMode ? {} : gradientBorderStyle) // Solo aplicar gradiente si NO es wallpaper
      },
      '&.Mui-focused': {
        border: isWallpaperMode
          ? '1px solid #FFFFFF'
          : '1px solid transparent',
        backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : colors.cardBackground,
        ...(isWallpaperMode ? {} : gradientBorderStyle) // Solo aplicar gradiente si NO es wallpaper
      }
    },
    '& .MuiInputBase-input': {
      minHeight: '25px',
      height: 'auto',
      padding: '0 24px 0 32px',                     
      fontFamily,
      fontSize: '17px',
      lineHeight: '25px',
      letterSpacing: '-0.022em',
      paddingRight: '70px !important',
      color: colors.textDefault,
      WebkitTextFillColor: colors.textDefault,
      '&::placeholder': {
        color: placeholderColor,
        opacity: 1,
        WebkitTextFillColor: placeholderColor
      },
      '&::-webkit-input-placeholder': {
        color: placeholderColor,
        opacity: 1,
        WebkitTextFillColor: placeholderColor
      },
    },
    [theme.breakpoints.down('sm')]: {
      margin: '8px 0',
      '& .MuiOutlinedInput-root': {
        minHeight: '48px',
        borderRadius: '20px',
      },
      '& .MuiInputBase-input': {
        fontSize: '16px',
        padding: '0 50px 0 16px',
        minHeight: '20px',
        paddingRight: '50px !important'
      }
    }
  };
});
  
export const SendButton = styled(IconButton)(({ theme }) => {
  const isWallpaper = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10,
    padding: '8px',
    // Blanco en wallpaper, azul en otros
    color: isWallpaper ? '#FFFFFF' : 'var(--ui-accent)',
    '&.Mui-disabled': {
      color: 'var(--system-grey)',
    },
    [theme.breakpoints.down('sm')]: {
      right: '8px',
      padding: '6px'
    }
  };
});

export const ClearButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '70px',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  padding: '8px',
  color: 'var(--fill-tertiary)',
  [theme.breakpoints.down('sm')]: {
    right: '44px',
    padding: '6px'
  }
}));

export const SectionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  gap: theme.spacing(4),
  fontFamily,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: '0 4px'
  }
}));

export const Section = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  fontFamily,
  [theme.breakpoints.down('sm')]: {
    gap: '12px'
  }
}));

export const CardsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  fontFamily,
  [theme.breakpoints.down('sm')]: {
    gap: '6px'
  }
}));

export const CustomCard = styled(MuiCard)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    width: '100%',
    cursor: 'pointer',
    borderRadius: '30px',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    fontFamily,
    backgroundColor: colors.cardBackground,
    border: `1px solid ${colors.cardBackground}`,
    '&:hover': {
      backgroundColor: colors.cardBackground,
      borderColor: colors.cardBackground
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '20px',
      border: 'none'
    }
  };
});

export const CustomCardContent = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: colors.cardBackground,
    '& .MuiTypography-body2': {
      fontFamily,
      fontSize: '17px',
      lineHeight: 1.47059,
      letterSpacing: '-0.022em',
      color: colors.textDefault,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '10px',
      gap: '8px',
      '& .MuiTypography-body2': {
        fontSize: '15px'
      }
    }
  };
});

export const StyledFooter = styled(Box)(({ theme }) => {
  // Usar background transparente o semi-transparente si es modo wallpaper
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: isWallpaperMode ? 'transparent' : 'var(--bg-primary-elevated)', // Completamente transparente en wallpaper
    backdropFilter: isWallpaperMode ? 'none' : 'none', // Sin blur para ver la imagen claramente
    zIndex: 1000,
    fontFamily,
    height: '69px', // Establecer altura explícitamente
    boxSizing: 'border-box', // Asegurar que el padding está incluido en la altura
    [theme.breakpoints.down('sm')]: {
      padding: '12px',
      paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)',
      height: '72px' // Establecer altura explícitamente para móvil
    }
  };
});

export const LinksContainer = styled(Box)({
  display: 'flex',
  gap: '32px',
  justifyContent: 'center',
  flex: 1,
});

export const FooterLink = styled(Link)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : '#86868B',
    textDecoration: 'none',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline',
      color: isWallpaperMode ? '#FFFFFF' : 'var(--ui-accent)'
    }
  };
});

export const HelpButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const iconColor = isWallpaperMode ? '#FFFFFF' : 'var(--ui-accent)';

  return {
    backgroundColor: 'transparent',
    color: iconColor,
    padding: '8px',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'var(--grouped-bg-primary-base)',
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      color: iconColor
    },
    '& svg': {
      stroke: iconColor,
      strokeWidth: 1.5,
    }
  };
});

export const WaitingMessage = styled(Typography)({
  marginTop: '16px',
  fontStyle: 'italic',
  fontWeight: '500',
  color: 'var(--fill-tertiary)',
  textAlign: 'center',
  width: '100%',
  animation: `${glowAnimation} 3s ease-in-out infinite`,
});

// Eliminamos LoadingIndicator ya que ahora usamos AppleSpinner

export const InputContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  '& .MuiInputBase-root': {
    '& > textarea': {
      paddingRight: '120px !important',
      boxSizing: 'border-box',
    }
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-root': {
      '& > textarea': {
        paddingRight: '80px !important',
      }
    }
  }
}));

// Nuevos componentes para las PromptCards
export const PromptCardItem = styled(Box, {
  shouldForwardProp: (prop) => !['selected', 'disabled'].includes(prop),
})(({ theme, selected, disabled }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'),
    borderRadius: '28px',
    padding: '10px 16px',
    marginBottom: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.6 : 1,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: selected
      ? `2px solid ${theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'}`
      : isWallpaperMode
        ? `1px solid rgba(128, 128, 128, 0.3)`
        : `2px solid transparent`,
    '&:hover': {
      backgroundColor: disabled
        ? (isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'))
        : (isWallpaperMode ? 'rgba(128, 128, 128, 0.5)' : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB')),
      transform: disabled ? 'none' : 'translateY(-2px)',
    },
    boxSizing: 'border-box',
  };
});

export const PromptIconWrapper = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
  };
});

export const PromptText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  flex: 1,
  fontSize: '14px',
  fontWeight: selected ? 600 : 500,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const CheckmarkIcon = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    color: '#FFFFFF',
    width: '14px',
    height: '14px',
  }
}));
